import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Language } from '@/api/types';
import { Button } from '@/components/ui/button.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { actionColor } from '@/lib/brandColor.ts';
import type { ContinueOption, PositionOption } from '../positionOptions';
import { ApplyFlowShell } from './ApplyFlowShell';
import { ContinueCard } from './ContinueCard';

interface ContinueGateProps {
  items: ContinueOption[];
  /**
   * Open roles with no interview in progress — the choices offered under "or
   * apply for a different position". Empty when every open role is already in
   * progress (or the property has none left), which drops that whole block:
   * an empty dropdown over a CTA that leads to an empty form is a dead end,
   * not an escape hatch.
   */
  available: PositionOption[];
  bgColor: string;
  inkColor: string;
  logoUrl: string;
  brandName?: string;
  language: Language;
  availableLanguages: Language[];
  onLanguageChange: (language: Language) => void;
  onContinue: (invitationKey: string) => void;
  /** Hands back a `position_id` for the form to open preselected. */
  onApplyOther: (positionId: string) => void;
}

/**
 * The gate shown before the apply form when the visitor's session has
 * unfinished interviews. It is a screen, not a modal, and has no URL of its
 * own — `ApplyJob` swaps it for the form when a different position is picked.
 *
 * Two distinct paths live here: each card continues its own interview, while
 * the picker below leads to the form for a role that has none.
 */
export function ContinueGate({
  items,
  available,
  bgColor,
  inkColor,
  logoUrl,
  brandName,
  language,
  availableLanguages,
  onLanguageChange,
  onContinue,
  onApplyOther,
}: ContinueGateProps) {
  const { t } = useTranslation('applyJob');
  const single = items.length === 1;
  const brandColor = actionColor(bgColor);
  const canApplyElsewhere = available.length > 0;

  // Local, not lifted: the choice only matters at the moment the CTA is
  // pressed, and `onApplyOther` hands it to the form's own field then. Holding
  // it in `ApplyJob` would mean two sources of truth for one position id.
  const [picked, setPicked] = useState('');

  return (
    <ApplyFlowShell
      bgColor={bgColor}
      inkColor={inkColor}
      logoUrl={logoUrl}
      brandName={brandName}
      welcomeTitle=""
      language={language}
      availableLanguages={availableLanguages}
      onLanguageChange={onLanguageChange}
      lockHero="full"
      footer={
        canApplyElsewhere ? (
          <Button
            type="button"
            variant="brand"
            size="lg"
            data-testid="continue-gate-apply"
            className="w-full max-w-md gap-2 rounded-xl"
            disabled={!picked}
            onClick={() => onApplyOther(picked)}
          >
            {t('continue_gate_apply_cta')}
            <ArrowRight className="size-4" aria-hidden />
          </Button>
        ) : null
      }
    >
      <div className="mt-4 flex flex-col">
        <span
          className="inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-extrabold tracking-widest"
          style={{ background: `${brandColor}16`, color: brandColor }}
        >
          <span
            className="size-1.5 rounded-full"
            style={{ background: brandColor }}
            aria-hidden
          />
          {t(single ? 'continue_gate_badge' : 'in_progress_group_label')}
        </span>

        <h1 className="mt-3.5 text-[23px] font-extrabold leading-tight tracking-tight text-[#16171a]">
          {t(single ? 'continue_gate_title_single' : 'continue_gate_title_multiple')}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[#6b7078]">
          {t(single ? 'continue_gate_subtitle_single' : 'continue_gate_subtitle_multiple')}
        </p>

        <div className="mt-5 flex flex-col gap-2.5">
          {items.map((item) => (
            <ContinueCard
              key={item.value}
              item={item}
              brandColor={brandColor}
              onContinue={() => onContinue(item.invitationKey)}
            />
          ))}
        </div>

        {canApplyElsewhere && (
          <div className="mt-6">
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-[#8a8f98]">
              {t('continue_gate_other_label')}
            </div>
            <Select value={picked} onValueChange={setPicked}>
              <SelectTrigger
                id="gate_position_id"
                data-testid="continue-gate-position"
                className="h-12 w-full rounded-md border-[1.5px] border-[#e3e4e8] font-medium text-[#16171a]"
                style={picked ? { borderColor: brandColor } : undefined}
              >
                <SelectValue placeholder={t('continue_gate_select_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {available.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </ApplyFlowShell>
  );
}
